import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Button, Container } from './site/ui';
import {
  IconArrowRight,
  IconCamera,
  IconCases,
  IconCourier,
  IconDashboard,
  IconDownload,
  IconFragile,
  IconGift,
  IconMerchants,
  IconNote,
  IconPackage,
  IconPin,
  IconRefresh,
  IconSearch,
  IconSend,
  IconUser
} from './icons';

/**
 * The Live Delivery Pulse — the three panels of real, customer-reported
 * figures.
 *
 * Shared rather than owned by the home page: each audience page shows the
 * panel that speaks to it (merchants see the retailer ranking, couriers the
 * carrier one), and the home page shows all three. One copy means the
 * disclaimer, the thresholds and the way a change is read can never differ
 * between the pages that show the same numbers.
 */

/**
 * The five reportable issues, in the order the report form offers them.
 *
 * Each one owns a colour and a glyph for the whole Customer panel: the tile a
 * customer taps to start a report and the bar that same issue turns into in
 * the figures below are the same colour, so the two halves read as one
 * picture rather than two unrelated charts. `tile` is the saturated companion
 * of `bar` — a 2.25rem circle with a white glyph needs the darker one.
 *
 * `key` must match the issue-type values stored on a Case, which is what both
 * the Pulse endpoint groups by and the report form prefills from.
 */
const ISSUE_TYPES = [
  {
    key: 'not_arrived',
    label: "Parcel hasn't arrived yet",
    icon: IconCourier,
    tile: 'bg-data-red-solid',
    bar: 'bg-data-red'
  },
  {
    key: 'delivered_not_received',
    label: 'Delivered but not received',
    icon: IconPackage,
    tile: 'bg-data-orange-solid',
    bar: 'bg-data-orange'
  },
  {
    key: 'missing_items',
    label: 'Missing items',
    icon: IconGift,
    tile: 'bg-data-purple-solid',
    bar: 'bg-data-purple'
  },
  {
    key: 'damaged',
    label: 'Damaged delivery',
    icon: IconFragile,
    tile: 'bg-data-blue-solid',
    bar: 'bg-data-blue'
  },
  {
    key: 'unsafe_place',
    label: 'Left in an unsafe place',
    icon: IconPin,
    tile: 'bg-data-grey-solid',
    bar: 'bg-data-grey'
  }
];

const ISSUE_BY_KEY = new Map(ISSUE_TYPES.map((t) => [t.key, t]));

/** What a customer gets for the two minutes, under the CTA. */
const customerAssurances = [
  { icon: IconNote, title: 'Free to report', detail: 'No account needed' },
  { icon: IconCamera, title: 'Upload evidence', detail: 'Photos, invoice etc.' },
  { icon: IconSend, title: 'We route it to', detail: 'the retailer' }
];

/** Windows the Pulse offers. Must match ALLOWED_DAYS on the server. */
const PULSE_WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' }
];

const merchantFeatures = [
  { icon: IconSearch, label: 'View all cases' },
  { icon: IconRefresh, label: 'Refund customers' },
  { icon: IconPackage, label: 'Reship orders' },
  { icon: IconCases, label: 'Replacement or investigate' }
];

const courierFeatures = [
  { icon: IconSearch, label: 'Postcode hotspots' },
  { icon: IconMerchants, label: 'Merchant breakdown' },
  { icon: IconDashboard, label: 'Issue trends & anomalies' },
  { icon: IconDownload, label: 'Reports and API (optional)' }
];

/**
 * Change against the previous window of the same length.
 *
 * More reported issues is not good news, so the arrow is read the opposite way
 * round from a sales chart: up is danger, down is success. Against a zero
 * baseline there is no percentage to state at all — that prints as words
 * rather than a made-up 0% or 100%.
 */
const ChangeArrow = ({ pct, compact = false }) => {
  if (pct == null) {
    // In a narrow table column the full phrase wraps and swamps the row, so
    // there it is a dash that explains itself on hover and to a screen reader.
    return compact ? (
      <span className="text-ink-400" title="No comparison yet — nothing reported in the previous period">
        —<span className="sr-only"> no comparison yet</span>
      </span>
    ) : (
      <span className="text-ink-500">— no comparison yet</span>
    );
  }
  if (pct === 0) {
    return <span className="font-600 text-ink-500">0%</span>;
  }
  const up = pct > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 font-600 ${up ? 'text-danger-600' : 'text-success-600'}`}>
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d={up ? 'M12 19V5M5 12l7-7 7 7' : 'M12 5v14M5 12l7 7 7-7'} />
      </svg>
      {Math.abs(pct)}%
    </span>
  );
};

/**
 * The ranked table both the Merchant and Courier panels show — identical but
 * for the column heading and the rows, so it lives here once.
 *
 * A name only reaches this list once it clears the server's `pulseMinCases`
 * threshold, so an empty table means "not enough reports yet", not "no
 * problems" — and it says so rather than showing an empty frame.
 */
const PulseRanking = ({ heading, rows, loading }) => {
  if (loading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((k) => (
          <div key={k} className="h-9 animate-pulse rounded-lg bg-ink-100" />
        ))}
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <p className="py-6 text-center text-[13px] text-ink-500">
        Not enough reports in this period to show a breakdown.
      </p>
    );
  }

  return (
    <div className="-mx-1 overflow-x-auto">
      <table className="min-w-full text-left text-[13px]">
        <thead>
          <tr className="border-b border-ink-200 text-[11px] uppercase tracking-[0.06em] text-ink-500">
            <th className="px-1 pb-2 font-600">#</th>
            <th className="px-1 pb-2 font-600">{heading}</th>
            <th className="px-1 pb-2 font-600">Most reported</th>
            <th className="px-1 pb-2 text-right font-600">Cases</th>
            <th className="px-1 pb-2 text-right font-600">Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.name} className="border-b border-ink-100 last:border-0">
              <td className="px-1 py-2 tabular-nums text-ink-500">{row.rank}</td>
              <td className="px-1 py-2 font-600 text-ink-900">{row.name}</td>
              <td className="px-1 py-2 text-[12px] leading-snug text-ink-600">{row.issue || '—'}</td>
              <td className="px-1 py-2 text-right font-600 tabular-nums text-ink-900">{row.cases}</td>
              <td className="px-1 py-2 text-right text-[12px]">
                <ChangeArrow pct={row.changePct} compact />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};


/** How often an open page re-checks for new reports. */
const REFRESH_MS = 60 * 1000;

/**
 * Fetches the Pulse for a window. Every panel on a page shares one request —
 * the endpoint returns all three panels' figures together.
 *
 * Refreshes itself while the tab is visible, so a page left open picks up
 * reports submitted after it loaded. A hidden tab polls nothing — a
 * backgrounded phone should not generate traffic — and refetches the moment
 * it comes back, rather than waiting out the rest of its interval.
 *
 * Background refreshes deliberately do not raise `loading`: flashing the
 * skeletons over figures that are already on screen, once a minute, reads as
 * a fault. Only the first load and a window change show them.
 */
export const usePulse = (days, limit) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchPulse = ({ showLoading }) => {
      if (showLoading) setLoading(true);
      return api
        .get(`/api/pulse?days=${days}${limit ? `&limit=${limit}` : ''}`)
        .then(({ data: payload }) => {
          if (!cancelled) setData(payload);
        })
        // On a background refresh the previous figures are left on screen:
        // a dropped poll is not a reason to blank a public page. Only the
        // first load has nothing to fall back to.
        .catch(() => {
          if (!cancelled && showLoading) setData(null);
        })
        .finally(() => {
          if (!cancelled && showLoading) setLoading(false);
        });
    };

    fetchPulse({ showLoading: true });

    const tick = () => {
      if (document.visibilityState === 'visible') fetchPulse({ showLoading: false });
    };
    const interval = setInterval(tick, REFRESH_MS);
    document.addEventListener('visibilitychange', tick);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [days, limit]);

  return { pulse: data, pulseLoading: loading };
};

/** The 7/30/90 switch. Every figure in every panel moves with it. */
const WindowPicker = ({ days, onChange }) => (
  <div
    className="inline-flex shrink-0 gap-1 rounded-xl bg-white p-1 ring-1 ring-ink-200"
    role="group"
    aria-label="Pulse time window"
  >
    {PULSE_WINDOWS.map((w) => (
      <button
        key={w.days}
        type="button"
        onClick={() => onChange(w.days)}
        aria-pressed={days === w.days}
        className={`rounded-lg px-3.5 py-1.5 text-[13px] font-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
          days === w.days ? 'bg-brand-800 text-white' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
        }`}
      >
        {w.label}
      </button>
    ))}
  </div>
);

/**
 * The frame every panel shares.
 *
 * Each panel is really two halves: the figures, and the pitch that follows
 * from them. Stacked in a three-up grid that reads top to bottom; on a page
 * showing one panel alone it would leave half the width empty, so there the
 * two halves sit side by side instead and the card fills the row.
 */
const PanelShell = ({ accent, icon: Icon, title, subtitle, days, wide, data, aside }) => (
  <div className="flex flex-col overflow-hidden rounded-2xl bg-white shadow-card ring-1 ring-ink-200">
    <div className={`flex items-center gap-3 px-5 py-4 ${accent.header}`}>
      <Icon size={20} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-display text-[17px] font-700">{title}</p>
        <p className={`text-xs ${accent.subtitle}`}>{subtitle}</p>
      </div>
      {/* Two lines, the same height as the title and subtitle it sits beside:
          what the feed is, then the window it covers. Every panel says it for
          itself rather than relying on the picker above the section still
          being on screen. */}
      <div className={`shrink-0 rounded-lg px-3 py-1.5 ring-1 ring-inset ${accent.pill}`}>
        <p className="flex items-center gap-1.5 text-[13px] font-700 leading-tight">
          <span
            className={`h-2 w-2 rounded-full motion-safe:animate-pulse ${accent.dot}`}
            aria-hidden="true"
          />
          Live data
        </p>
        <p className={`text-[11px] leading-tight ${accent.subtitle}`}>Last {days} days</p>
      </div>
    </div>

    <div
      className={
        wide
          ? 'grid flex-1 gap-6 p-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:gap-8 lg:p-6'
          : 'flex flex-1 flex-col p-5'
      }
    >
      <div className="flex min-w-0 flex-col">{data}</div>
      {/* Pushed to the bottom when stacked so the CTA lines up across a row
          of panels. When wide it is a short rail beside a tall chart, so it
          centres against it rather than sitting at the top over a gap. */}
      <div
        className={
          wide
            ? 'flex min-w-0 flex-col lg:justify-center lg:border-l lg:border-ink-100 lg:pl-8'
            : 'mt-auto flex flex-col pt-4'
        }
      >
        {aside}
      </div>
    </div>
  </div>
);

/**
 * Every issue type, reported or not — the ones with reports first in the
 * order the server ranked them, then the rest at 0%.
 *
 * The endpoint only groups what was actually reported, so a quiet period
 * would otherwise show one bar at 100% and nothing else, which reads as "this
 * is the only thing that can go wrong". Showing the full five at their real
 * share keeps the list the same shape as the tiles above it.
 *
 * Labels come from ISSUE_TYPES rather than the payload so a bar and the tile
 * that starts that report never word the same issue two different ways.
 */
const issueRows = (pulse) => {
  const reported = (pulse?.topIssues || []).map((issue) => {
    const type = ISSUE_BY_KEY.get(issue.key);
    return { ...issue, label: type?.label || issue.label, bar: type?.bar || 'bg-data-grey' };
  });
  const seen = new Set(reported.map((r) => r.key));
  return [
    ...reported,
    ...ISSUE_TYPES.filter((t) => !seen.has(t.key)).map((t) => ({
      key: t.key,
      label: t.label,
      bar: t.bar,
      count: 0,
      pct: 0
    }))
  ];
};

/**
 * The issue picker: five tiles that are both the legend for the bars below
 * and the first step of a report, since tapping one carries the choice into
 * the form rather than dropping the customer on an empty page.
 */
const IssueTiles = ({ wide }) => {
  // Five across — the design — needs about 70px a tile before the longer
  // labels start wrapping to four lines. That is there at sm and above when
  // the panel has the row to itself, but not on a phone, and not in the
  // three-up grid where a panel is a third of the page: both of those drop
  // to two columns and turn the tile on its side, where a label has the
  // width to break in a sensible place.
  const narrowAgain = wide ? '' : ' lg:grid-cols-2 lg:last:col-span-2';
  return (
    <div className="rounded-2xl bg-ink-50 p-3.5">
      <p className="font-display text-[15px] font-700 text-ink-900">
        What went wrong with your delivery?
      </p>
      <ul className={`mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5${wide ? '' : ' lg:grid-cols-2'}`}>
        {ISSUE_TYPES.map(({ key, label, icon: Icon, tile }) => (
          <li key={key} className={`flex last:col-span-2 sm:last:col-span-1${narrowAgain}`}>
            <Link
              to="/report"
              state={{ issueType: key }}
              className={`flex w-full items-center gap-2.5 rounded-xl bg-white p-2.5 text-left shadow-card ring-1 ring-ink-200 transition hover:-translate-y-0.5 hover:ring-brand-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 sm:flex-col sm:gap-2 sm:p-2.5 sm:text-center${
                wide ? '' : ' lg:flex-row lg:gap-2.5 lg:text-left'
              }`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white ${tile}`}>
                <Icon size={18} strokeWidth={2} />
              </span>
              <span className="min-w-0 text-[11px] font-600 leading-tight text-ink-700">{label}</span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CustomerPanel = ({ pulse, pulseLoading, pulseDays, wide }) => (
  <PanelShell
    wide={wide}
    icon={IconUser}
    title="Customer Pulse"
    subtitle="What are customers reporting?"
    days={pulseDays}
    accent={{
      header: 'bg-brand-700 text-white',
      subtitle: 'text-white/75',
      pill: 'bg-white/15 text-white ring-white/25',
      dot: 'bg-success-400'
    }}
    data={
      <>
        <IssueTiles wide={wide} />

        {/* Same recessed well as the tiles, directly beneath them: the
            figures are the answer to the question the tiles just asked. */}
        <div className="mt-3 rounded-2xl bg-ink-50 p-3.5">
          {/* No "live data" badge of its own here any more — the panel's
              header carries one, and two of them a hundred pixels apart was
              the same sentence twice. */}
          <p className="text-[14px] text-ink-700">Customer reports in the last {pulseDays} days</p>

          <div className="mt-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <p className="font-display text-[40px] font-800 leading-none tracking-tight text-ink-900">
              {pulseLoading ? '—' : (pulse?.totals.current ?? 0).toLocaleString('en-GB')}
            </p>
            {!pulseLoading && (
              <p className="flex items-baseline gap-1.5 text-[14px]">
                <ChangeArrow pct={pulse?.totals.changePct} />
                {/* "no comparison yet" already says there is nothing to compare
                    against, so the period is only named when a figure needs it. */}
                {pulse?.totals.changePct != null && (
                  <span className="text-ink-500">vs previous {pulseDays} days</span>
                )}
              </p>
            )}
          </div>

          <p className="mt-4 text-[14px] text-ink-700">Top reported issues</p>
          {pulseLoading ? (
            <ul className="mt-3 space-y-3" aria-hidden="true">
              {ISSUE_TYPES.map((t) => (
                <li key={t.key} className="h-2.5 animate-pulse rounded-full bg-ink-200" />
              ))}
            </ul>
          ) : (
            <ul className="mt-3 space-y-3">
              {issueRows(pulse).map((issue) => (
                <li
                  key={issue.key}
                  className="grid grid-cols-[minmax(0,1fr)_minmax(56px,0.9fr)_2.5rem] items-center gap-x-3"
                >
                  <span className="text-[14px] leading-snug text-ink-600">{issue.label}</span>
                  {/* The share is what the row shows; the count behind it is one
                      hover (or one screen-reader stop) away rather than a second
                      number competing with the total above. */}
                  <div
                    className="h-2.5 w-full overflow-hidden rounded-full bg-ink-200"
                    title={`${issue.count} report${issue.count === 1 ? '' : 's'}`}
                  >
                    <div
                      className={`h-full rounded-full ${issue.bar}`}
                      style={{ width: `${issue.pct}%` }}
                    />
                  </div>
                  <span className="text-right text-[14px] font-500 tabular-nums text-ink-700">
                    {issue.pct}%
                    <span className="sr-only">, {issue.count} reports</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </>
    }
    aside={
      <>
        {wide && (
          <p className="text-[13px] leading-relaxed text-ink-600">
            Tell us what happened and we’ll capture the details and evidence, then route it to the
            retailer that can actually resolve your issue. It takes about two minutes.
          </p>
        )}
        {/* Flows under the text when wide: pinning it to the bottom of a
            rail shorter than the chart beside it just opened a gap. Stacked,
            it stays pinned by the wrapper so CTAs line up across the grid. */}
        <div className={wide ? 'mt-5' : ''}>
          <Button as={Link} to="/report" size="lg" className="w-full">
            Report a Delivery Issue FREE
            <IconArrowRight size={18} />
          </Button>
          {/* Three answers to the three things that stop someone reporting:
              what it costs, what they have to bring, and where it ends up. */}
          <ul className="mt-4 grid grid-cols-3 gap-x-2 gap-y-3">
            {customerAssurances.map(({ icon: Icon, title, detail }) => (
              <li key={title} className="flex items-start gap-1.5">
                <Icon size={15} className="mt-px shrink-0 text-brand-600" />
                <span className="min-w-0 text-[10.5px] leading-tight">
                  <span className="block font-700 text-ink-900">{title}</span>
                  <span className="block text-ink-500">{detail}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </>
    }
  />
);

const MerchantPanel = ({ pulse, pulseLoading, pulseDays, wide }) => (
  <PanelShell
    wide={wide}
    icon={IconMerchants}
    title="Merchant Pulse"
    subtitle="Top retailers with customer-reported issues"
    days={pulseDays}
    accent={{
      header: 'bg-gold-500 text-brand-900',
      subtitle: 'text-brand-900/70',
      pill: 'bg-white/35 text-brand-900 ring-brand-900/10',
      dot: 'bg-success-400'
    }}
    data={<PulseRanking heading="Retailer" rows={pulse?.merchants} loading={pulseLoading} />}
    aside={
      <>
        <div className="rounded-xl bg-gold-50 p-3.5 ring-1 ring-inset ring-gold-100">
          <p className="text-[13px] font-700 text-ink-900">Are you one of these retailers?</p>
          <p className="mt-1 text-[12px] leading-relaxed text-ink-600">
            Your customers may already be reporting delivery issues through ParcelBuddy. Claim your
            profile to see cases, evidence and postcode insights — and act on them.
          </p>
        </div>

        <div className={wide ? 'mt-5' : 'mt-auto pt-4'}>
          {/* Pricing, not the marketing page: someone who has just seen their
              own shop in this table is choosing a plan, and the tab is
              pre-selected for them. */}
          <Button as={Link} to="/pricing?for=merchants" variant="gold" className="w-full">
            Claim Your Merchant Profile
            <IconArrowRight size={16} />
          </Button>
          <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            {merchantFeatures.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-1.5 text-[11px] leading-tight text-ink-600">
                <Icon size={13} className="shrink-0 text-gold-600" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </>
    }
  />
);

const CourierPanel = ({ pulse, pulseLoading, pulseDays, wide }) => (
  <PanelShell
    wide={wide}
    icon={IconCourier}
    title="Courier Pulse"
    subtitle="Customer-reported delivery issues by courier"
    days={pulseDays}
    accent={{
      header: 'bg-info-600 text-white',
      subtitle: 'text-white/75',
      pill: 'bg-white/15 text-white ring-white/25',
      dot: 'bg-success-400'
    }}
    data={<PulseRanking heading="Courier" rows={pulse?.couriers} loading={pulseLoading} />}
    aside={
      <>
        <div className="flex items-start gap-2.5 rounded-xl bg-info-50 p-3.5 ring-1 ring-inset ring-info-100">
          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-info-600 text-white">
            <IconSearch size={13} />
          </span>
          <p className="text-[12px] leading-relaxed text-ink-700">
            {pulseLoading ? (
              'Counting postcode activity…'
            ) : pulse?.hotspotCount ? (
              <>
                <span className="font-700 text-ink-900">
                  {pulse.hotspotCount} postcode area{pulse.hotspotCount === 1 ? '' : 's'}
                </span>{' '}
                with customer-reported activity in the last {pulseDays} days.
              </>
            ) : (
              'No postcode activity to report in this period.'
            )}
          </p>
        </div>

        <div className={wide ? 'mt-5' : 'mt-auto pt-4'}>
          <Button as={Link} to="/pricing?for=couriers" variant="info" className="w-full">
            Unlock Courier Intelligence
            <IconArrowRight size={16} />
          </Button>
          <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
            {courierFeatures.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-1.5 text-[11px] leading-tight text-ink-600">
                <Icon size={13} className="shrink-0 text-info-600" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </>
    }
  />
);

const PANELS = { customer: CustomerPanel, merchant: MerchantPanel, courier: CourierPanel };

/**
 * @param {string[]} panels  which of customer/merchant/courier to render.
 *   Three of them is the home page's full set; an audience page passes one.
 */
const PulseSection = ({
  panels = ['customer', 'merchant', 'courier'],
  title = 'ParcelBuddy Live Delivery Pulse',
  description = 'Real customer-reported delivery issues, updated as they come in.',
  className = 'bg-ink-50 py-12 sm:py-14'
}) => {
  // 30 days by default — long enough to be stable, short enough to still be
  // "what is happening now".
  const [pulseDays, setPulseDays] = useState(30);

  const solo = panels.length === 1;

  // The home page shows a top five beside another panel; an audience page
  // gives the table a whole screen, so it asks for everything that qualifies
  // (the server caps it at 50).
  const { pulse, pulseLoading } = usePulse(pulseDays, solo ? 50 : undefined);

  // One row, a column per panel. A panel on its own would leave half the row
  // empty, so there it lays itself out horizontally instead — see PanelShell.
  const rendered = panels
    .filter((key) => PANELS[key])
    .map((key) => {
      const Panel = PANELS[key];
      return (
        <Panel
          key={key}
          pulse={pulse}
          pulseLoading={pulseLoading}
          pulseDays={pulseDays}
          wide={solo}
        />
      );
    });

  return (
    <section className={className}>
      <Container width="full">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-800 text-white">
              <IconDashboard size={20} />
            </span>
            <div>
              <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
                {title}
              </h2>
              <p className="mt-1 text-[15px] text-ink-600">{description}</p>
            </div>
          </div>
          <WindowPicker days={pulseDays} onChange={setPulseDays} />
        </div>

        <p className="mt-3 max-w-3xl text-xs leading-relaxed text-ink-500">
          Based on delivery issues customers submitted through ParcelBuddy in the last {pulseDays}{' '}
          days. These are reports, not verified outcomes — they do not measure courier or retailer
          performance, and do not establish responsibility for any delivery.
        </p>

        <div
          className={`mt-7 grid grid-cols-1 gap-5 ${
            rendered.length === 3 ? 'lg:grid-cols-3' : rendered.length === 2 ? 'lg:grid-cols-2' : ''
          }`}
        >
          {rendered}
        </div>
      </Container>
    </section>
  );
};

export default PulseSection;
